import { Machine, assign, EventObject } from 'xstate';

/**
 * @see https://xstate.js.org/docs/
 */
export class GlobalSearchContext
{
    searchValue: string = '';
    isSearchActivated: boolean = false;
    isPanelShown: boolean = false;
    isFocused: boolean = false;
}
export interface GlobalSearchStatesSchema
{
    states: {
        inactive: any,
        focus: any,
        active: {
            states: {
                shown: any;
                hidden: any;
            }
        }
    };
}
export enum GlobalSearchEventType {
    LAUNCH = 'LAUNCH',
    CLEAR = 'CLEAR',
    ESCAPE = 'ESCAPE',
    SHOW = 'SHOW',
    HIDE = 'HIDE',
    CTRL_SHIFT_F = 'CTRL_SHIFT_F',
    FOCUS = 'FOCUS'
}
type GlobalSearchEventId = GlobalSearchEventType.LAUNCH
    | GlobalSearchEventType.CTRL_SHIFT_F
    | GlobalSearchEventType.CLEAR
    | GlobalSearchEventType.ESCAPE
    | GlobalSearchEventType.SHOW
    | GlobalSearchEventType.HIDE
    | GlobalSearchEventType.FOCUS;
interface GlobalSearchEvent extends EventObject
{
    type: GlobalSearchEventId;
    ctxt: GlobalSearchContext;
}

export const GlobalSearchMachine = Machine<GlobalSearchContext, GlobalSearchStatesSchema, GlobalSearchEvent>(
    {
        id: 'GlobalSearch',
        initial: 'inactive',
        context: new GlobalSearchContext(),
        states: {
            inactive: {
                id: 'inactive',
                on: {
                    LAUNCH: {
                        target: 'active.shown',
                        actions: ['showResultsPanel', 'setSearchValue']
                    },
                    FOCUS: {
                        target: '#focus',
                        actions: ['setFocus']
                    }
                }
            },
            focus: {
                id: 'focus',
                on: {
                    LAUNCH: {
                        target: 'active.shown',
                        actions: ['showResultsPanel', 'setSearchValue']
                    }
                }
            },

            active: {
                id: 'active',
                states: {
                    shown: {
                        id: 'active.shown',
                        on: {
                            LAUNCH: {
                                target: 'shown',
                                actions: ['showResultsPanel', 'setSearchValue']
                            },
                            CLEAR: {
                                target: '#inactive',
                                actions: ['hideResultsPanel', 'resetSearchValue', 'resetFocus']
                            },
                            HIDE: {
                                target: '#active.hidden',
                                actions: 'hideResultsPanel'
                            },
                            ESCAPE: {
                                target: '#active.hidden',
                                actions: 'hideResultsPanel'
                            }
                        }
                    },
                    hidden: {
                        id: 'active.hidden',
                        on: {
                            LAUNCH: {
                                target: '#active.shown',
                                actions: ['showResultsPanel', 'setSearchValue']
                            },
                            CLEAR: {
                                target: '#inactive',
                                actions: ['hideResultsPanel', 'resetSearchValue', 'resetFocus']
                            },
                            SHOW: {
                                target: '#active.shown',
                                actions: ['showResultsPanel', 'setFocus']
                            },
                            CTRL_SHIFT_F: {
                                target: '#active.shown',
                                actions: ['showResultsPanel', 'setFocus']
                            }
                        }
                    }
                }
            }
        }
    },
    {
        actions: {
            log: (context, event) => console.debug({ context, event }),
            setFocus: assign<GlobalSearchContext, GlobalSearchEvent>({
                isFocused: () => true
            }),
            resetFocus: assign<GlobalSearchContext, GlobalSearchEvent>({
                isFocused: () => false
            }),
            setSearchValue: assign<GlobalSearchContext, GlobalSearchEvent>({
                searchValue: (_, event: GlobalSearchEvent) => event.ctxt.searchValue,
                isSearchActivated: (_, event: GlobalSearchEvent) => event.ctxt.searchValue.length > 2
            }),
            resetSearchValue: assign<GlobalSearchContext, GlobalSearchEvent>({
                searchValue: () => '',
                isSearchActivated:  () => false
            }),
            showResultsPanel: assign<GlobalSearchContext, GlobalSearchEvent>({
                isPanelShown: () => true
            }),
            hideResultsPanel: assign<GlobalSearchContext, GlobalSearchEvent>({
                isPanelShown: () => false
            })
        }
    });
